import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { getTeacherCohortQuizResults, type TeacherCohortQuizResultAttempt } from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";

export default function TeacherQuizResultsPage() {
  const { cohortId, quizId } = useParams<{ cohortId: string; quizId: string }>();
  const [searchParams] = useSearchParams();
  const studentFilter = searchParams.get("student") ?? "";
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quizTitle, setQuizTitle] = useState("");
  const [attempts, setAttempts] = useState<TeacherCohortQuizResultAttempt[]>([]);

  const load = useCallback(async () => {
    if (!bearer || !cohortId || !quizId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getTeacherCohortQuizResults(bearer, cohortId, quizId, {
        studentUserId: studentFilter || undefined,
      });
      setQuizTitle(data.quizTitle);
      setAttempts(data.attempts);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load quiz results");
    } finally {
      setLoading(false);
    }
  }, [bearer, cohortId, quizId, studentFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const byStudent = new Map<string, TeacherCohortQuizResultAttempt[]>();
  for (const a of attempts) {
    const list = byStudent.get(a.studentUserId) ?? [];
    list.push(a);
    byStudent.set(a.studentUserId, list);
  }

  return (
    <div className="teacher-page teacher-pad">
      <header className="teacher-quiz-results-head">
        <h1 className="teacher-panel-title">{quizTitle || "Quiz"} results</h1>
        <Link className="btn secondary tight" to={`/teacher/cohorts/${encodeURIComponent(cohortId ?? "")}`}>
          Back to cohort
        </Link>
      </header>
      {loading ? <p className="muted">Loading…</p> : null}
      {error ? <p className="teacher-panel-error">{error}</p> : null}
      {!loading && !error && byStudent.size === 0 ? (
        <p className="muted">No student attempts yet.</p>
      ) : null}
      {!loading && !error && byStudent.size > 0 ? (
        <div className="teacher-quiz-results-list">
          {[...byStudent.entries()].map(([sid, rows]) => {
            const email = rows[0]?.email ?? sid;
            const a1 = rows.find((r) => r.attemptNumber === 1);
            const a2 = rows.find((r) => r.attemptNumber === 2);
            const best = Math.max(a1?.scorePercent ?? 0, a2?.scorePercent ?? 0);
            return (
              <article key={sid} className="teacher-quiz-results-card">
                <h2>{email}</h2>
                <p>
                  {a1 ? `First try: ${a1.scorePercent}% (${a1.correctCount}/${a1.totalCount})` : "No attempts"}
                  {a2 ? ` · Retake: ${a2.scorePercent}% (${a2.correctCount}/${a2.totalCount})` : ""}
                  {a1 || a2 ? ` · Best: ${best}%` : ""}
                </p>
              </article>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
